/* Size guide wall visualizer: draws the product's set at true scale in a room
   drawn in code. All geometry comes from #size-guide-visualizer-config. */
(function () {
  'use strict';

  /* ---------- Pure geometry / formatting (exported for tests) ---------- */

  function parseSize(value) {
    var m = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i.exec(value || '');
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  function pieceDims(size, framed, borderIn) {
    var add = framed ? 2 * borderIn : 0;
    return { w: size.w + add, h: size.h + add };
  }

  function setDims(size, framed, count, cfg) {
    var p = pieceDims(size, framed, cfg.frameBorderIn);
    return { w: count * p.w + (count - 1) * cfg.pieceGapIn, h: p.h, piece: p };
  }

  function sofaBackTop(cfg) {
    if (typeof cfg.sofaBackTopY === 'number') return cfg.sofaBackTopY;
    return cfg.floorY - cfg.sofa.backHeightIn * cfg.pxPerInch;
  }

  // Set rectangle in natural pixels.
  function placement(set, cfg) {
    var ppi = cfg.pxPerInch;
    var w = set.w * ppi;
    var h = set.h * ppi;
    // Photo scenes give the art zone directly; the drawn room derives it from the sofa.
    var backTop = typeof cfg.artBottomY === 'number' ? cfg.artBottomY : sofaBackTop(cfg);
    var bottom = typeof cfg.artBottomY === 'number' ? cfg.artBottomY : backTop - cfg.artGapAboveSofaIn * ppi;
    var top = bottom - h;
    var minTop = typeof cfg.topLimitY === 'number' ? cfg.topLimitY : cfg.ceilingY + cfg.minCeilingClearanceIn * ppi;
    // Vignette scenes centre the art at a hanging height instead of resting it on the furniture;
    // artBottomY then acts as the lowest allowed bottom edge.
    if (typeof cfg.artCenterY === 'number') {
      top = cfg.artCenterY - h / 2;
      if (top + h > bottom) top = bottom - h;
      bottom = top + h;
    }
    if (top < minTop) {
      top = minTop;
      bottom = top + h;
    }
    var x = cfg.anchorX - w / 2;
    return {
      x: x, y: top, w: w, h: h,
      fitsVertically: top >= minTop - 0.5 && bottom <= backTop + 0.5,
      fitsZone: x >= cfg.zone.left && x + w <= cfg.zone.right
    };
  }

  function formatFtIn(inches) {
    var total = Math.round(inches);
    var ft = Math.floor(total / 12);
    var rest = total % 12;
    if (ft === 0) return rest + ' in';
    return ft + ' ft' + (rest ? ' ' + rest + ' in' : '');
  }

  function formatCm(inches) {
    return Math.round(inches * 2.54) + ' cm';
  }

  function formatLength(inches, units) {
    return units === 'cm' ? formatCm(inches) : formatFtIn(inches);
  }

  function lumAt(x, profile) {
    var xs = profile.x, ls = profile.lum;
    if (x <= xs[0]) return ls[0];
    if (x >= xs[xs.length - 1]) return ls[ls.length - 1];
    for (var i = 1; i < xs.length; i++) {
      if (x <= xs[i]) {
        var t = (x - xs[i - 1]) / (xs[i] - xs[i - 1]);
        return ls[i - 1] + t * (ls[i] - ls[i - 1]);
      }
    }
    return ls[ls.length - 1];
  }

  function lightingFactor(x, cfg) {
    var f = lumAt(x, cfg.lumProfile) / lumAt(cfg.anchorX, cfg.lumProfile);
    return Math.min(1.18, Math.max(0.78, f));
  }

  // Source rectangle for drawing a mockup image: crop its margin, then cover-fit.
  function sourceCrop(imgW, imgH, destW, destH, inset) {
    var ix = imgW * (inset || 0), iy = imgH * (inset || 0);
    var sx = ix, sy = iy, sw = imgW - 2 * ix, sh = imgH - 2 * iy;
    var sr = sw / sh, dr = destW / destH;
    if (sr > dr) { var nw = sh * dr; sx += (sw - nw) / 2; sw = nw; }
    else { var nh = sw / dr; sy += (sh - nh) / 2; sh = nh; }
    return { sx: sx, sy: sy, sw: sw, sh: sh };
  }

  // Fit helper.
  // Above furniture: art reads best at roughly 2/3-3/4 of the furniture it hangs above,
  // so recommend the largest size inside that band (or the closest if none fits).
  // Open wall: recommend statement-scale pieces with clear margins. If this
  // layout only fits small prints, suggest fewer, larger pieces instead.
  var FIT_BAND = { low: 0.6, high: 0.8 };
  var WALL_MIN_PIECE_WIDTH_IN = 24; // minimum statement size for an open wall
  var WALL_MARGIN_IN = 12;       // preferred clearance when choosing between sizes
  var WALL_MIN_MARGIN_IN = 6;    // fallback only when no statement size has preferred clearance
  var SPACIOUS_COVERAGE = 0.45;  // below this, the largest set reads as a gallery-style statement

  function recommendSize(sizeValues, count, framed, cfg, widthIn, mode, includeRelated) {
    if (!(widthIn > 0)) return null;
    var wall = mode === 'wall';
    var rows = [];
    sizeValues.forEach(function (value) {
      var size = parseSize(value);
      if (!size) return;
      var setW = setDims(size, framed, count, cfg).w;
      var ratio = setW / widthIn;
      var verdict;
      if (wall) verdict = setW > widthIn - 2 * WALL_MIN_MARGIN_IN ? 'wide' :
        (size.w < WALL_MIN_PIECE_WIDTH_IN ? 'small' :
          (setW <= widthIn - 2 * WALL_MARGIN_IN ? 'good' : 'tight'));
      else verdict = ratio < FIT_BAND.low ? 'small' : (ratio > FIT_BAND.high ? 'wide' : 'good');
      rows.push({ value: value, setW: setW, ratio: ratio, verdict: verdict });
    });
    if (!rows.length) return null;
    var good = rows.filter(function (r) { return r.verdict === 'good'; });
    var eligible = good.length ? good : rows.filter(function (r) { return r.verdict === 'tight'; });
    var best;
    if (eligible.length) {
      best = eligible.reduce(function (a, b) { return b.setW > a.setW ? b : a; });
    } else if (wall) {
      var withinMargins = rows.filter(function (r) { return r.verdict !== 'wide'; });
      best = withinMargins.length
        ? withinMargins.reduce(function (a, b) { return b.setW > a.setW ? b : a; })
        : rows.reduce(function (a, b) { return b.setW < a.setW ? b : a; });
    } else {
      var dist = function (r) { return r.ratio < FIT_BAND.low ? FIT_BAND.low - r.ratio : r.ratio - FIT_BAND.high; };
      best = rows.reduce(function (a, b) { return dist(b) < dist(a) ? b : a; });
    }
    best.best = !wall || eligible.length > 0;
    var largest = rows.reduce(function (a, b) { return b.setW > a.setW ? b : a; });
    var spacious = wall && eligible.length > 0 && best === largest && best.ratio < SPACIOUS_COVERAGE;
    var alternatives = [];
    var expansion = null;
    // Related layouts use exactly the same rules as visiting their own product page.
    // Disable related lookups on those calls to avoid recursive suggestions.
    if (wall && includeRelated !== false) {
      for (var otherCount = 1; otherCount <= 3; otherCount++) {
        if ((!eligible.length && otherCount < count) || (spacious && otherCount > count)) {
          var related = recommendSize(sizeValues, otherCount, framed, cfg, widthIn, mode, false);
          if (!related || !related.fits) continue;
          var candidate = {
            value: related.best.value, count: otherCount, setW: related.best.setW,
            sideIn: related.sideIn, tight: related.tight
          };
          if (otherCount < count) alternatives.unshift(candidate);
          else if (candidate.setW > best.setW && (!expansion || candidate.setW > expansion.setW)) expansion = candidate;
        }
      }
    }
    return {
      best: best, rows: rows, alternatives: alternatives, expansion: expansion,
      mode: wall ? 'wall' : 'furniture', fits: eligible.length > 0,
      tight: wall && best.verdict === 'tight',
      betweenSizes: !wall && !eligible.length && rows.some(function (r) { return r.verdict === 'small'; }) &&
        rows.some(function (r) { return r.verdict === 'wide'; }),
      sideIn: (widthIn - best.setW) / 2,
      spacious: spacious
    };
  }

  // Buying guide: compare every layout independently of the preview selection.
  function recommendArrangement(sizeValues, framed, cfg, widthIn, mode, prices) {
    if (!(widthIn > 0) || !isFinite(widthIn)) return null;
    var candidates = [];
    var wall = mode === 'wall';
    // Smaller artwork is only an exception for a wall too narrow for even one
    // 24-inch canvas with its frame and minimum six-inch side clearances.
    var narrowWall = wall && widthIn < pieceDims({ w: 24, h: 36 }, framed, cfg.frameBorderIn).w + 2 * WALL_MIN_MARGIN_IN;
    function priceFor(value, count) {
      var size = parseSize(value);
      var variants = prices && prices[count] || [];
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        var matchesSize = opts.some(function (o) { var s = parseSize(o); return s && s.w === size.w && s.h === size.h; });
        var matchesFinish = opts.some(function (o) { return framed ? /premium frame/i.test(o) : /gallery wrapped|canvas/i.test(o); });
        if (matchesSize && matchesFinish && variants[i].price > 0) return variants[i].price;
      }
      return null;
    }
    for (var n = 1; n <= 3; n++) {
      var rec = recommendSize(sizeValues, n, framed, cfg, widthIn, mode, false);
      if (!rec) continue;
      rec.rows.forEach(function (r) {
        var sideIn = (widthIn - r.setW) / 2;
        var verdict = r.verdict;
        // The standalone guide allows a fuller composition above furniture.
        // 86% accommodates a nominal 85% fit after frame/metric rounding.
        if (!wall) verdict = r.ratio < 0.6 ? 'small' : r.ratio <= 0.86 ? 'good' : 'wide';
        if (narrowWall && n === 1 && parseSize(r.value).w < 24 && sideIn >= WALL_MIN_MARGIN_IN) {
          verdict = sideIn >= WALL_MARGIN_IN ? 'good' : 'tight';
        }
        candidates.push({ value: r.value, count: n, setW: r.setW, ratio: r.ratio,
          verdict: verdict, sideIn: sideIn, price: priceFor(r.value, n) });
      });
    }
    function rank(r) { return r.verdict === 'good' ? 0 : r.verdict === 'tight' ? 1 : 2; }
    function fitOrder(a, b) {
      var tier = rank(a) - rank(b);
      if (tier) return tier;
      if (wall) {
        if (rank(a) < 2) return b.setW - a.setW || parseSize(b.value).w - parseSize(a.value).w || a.count - b.count;
        if ((a.verdict === 'wide') !== (b.verdict === 'wide')) return a.verdict === 'wide' ? 1 : -1;
        return a.verdict === 'wide' ? a.setW - b.setW : b.setW - a.setW;
      }
      return Math.abs(a.ratio - 0.75) - Math.abs(b.ratio - 0.75) || parseSize(b.value).w - parseSize(a.value).w || a.count - b.count;
    }
    candidates.sort(fitOrder);
    if (!candidates.length) return null;
    var geometricBest = candidates[0];
    // Only trade up among genuinely similar fits: same fit tier, within 15%
    // of the widest wall arrangement or 8 percentage points of furniture fit.
    var equivalent = candidates.filter(function (r) {
      if (rank(r) >= 2 || rank(r) !== rank(geometricBest)) return false;
      return wall ? r.setW >= geometricBest.setW * 0.85 :
        Math.abs(r.ratio - 0.75) <= Math.abs(geometricBest.ratio - 0.75) + 0.08;
    });
    var best = geometricBest;
    // Missing prices must not turn a geometry guess into an AOV claim.
    if (equivalent.length && equivalent.every(function (r) { return r.price !== null; })) {
      equivalent.sort(function (a, b) { return b.price - a.price || fitOrder(a, b); });
      best = equivalent[0];
    }
    var layouts = candidates.filter(function (r) {
      if (rank(r) >= 2) return false;
      // Offer alternatives with a comparable presence, not every smaller fit.
      return wall ? r.setW >= best.setW * 0.8 : Math.abs(r.ratio - best.ratio) <= 0.20;
    });
    if (!layouts.length) {
      var seen = {};
      layouts = candidates.filter(function (r) { if (seen[r.count]) return false; seen[r.count] = true; return true; });
    }
    layouts.sort(function (a, b) { return a === best ? -1 : b === best ? 1 : Math.abs(a.setW - best.setW) - Math.abs(b.setW - best.setW) || fitOrder(a, b); });
    layouts = layouts.slice(0, 3);
    return { best: best, layouts: layouts, fits: rank(best) < 2, mode: wall ? 'wall' : 'furniture', narrowWall: narrowWall };
  }

  // Scene key for a set: "<pieces>-large" for 24x36/40x60, "<pieces>-small" for 12x16/18x24.
  function sceneKey(size, count) {
    return count + '-' + (size && size.w >= 24 ? 'large' : 'small');
  }

  // Effective drawing config: the base config with a matching photo scene layered on top.
  function sceneConfig(base, size, count) {
    var selectedSceneKey = sceneKey(size, count);
    var scene = base.scenes && base.scenes[selectedSceneKey];
    var sharedFourPieceScene = false;
    if (!scene && count === 4 && size && base.scenes) {
      var sharedKey = sceneKey(size, 3);
      if (base.scenes[sharedKey]) {
        selectedSceneKey = sharedKey;
        scene = base.scenes[sharedKey];
        sharedFourPieceScene = true;
      }
    }
    if (!scene) {
      // Keep one stable camera for unsupported layouts, using the largest standard
      // size so switching sizes does not resize the art relative to the furniture.
      var maxSet = setDims({ w: Math.max(40, size ? size.w : 40), h: 60 }, true, count, base);
      var centeredWidth = 2 * Math.min(base.anchorX - base.zone.left, base.zone.right - base.anchorX);
      var extraWidth = Math.max(0, maxSet.w * base.pxPerInch - centeredWidth);
      if (!extraWidth) return base;
      var drawn = {};
      Object.keys(base).forEach(function (k) { drawn[k] = base[k]; });
      drawn.naturalWidth = base.naturalWidth + extraWidth;
      drawn.anchorX = base.anchorX + extraWidth / 2;
      drawn.zone = { left: base.zone.left, right: base.zone.right + extraWidth };
      if (base.person) {
        drawn.person = {};
        Object.keys(base.person).forEach(function (k) { drawn.person[k] = base.person[k]; });
        drawn.person.centerX += extraWidth;
      }
      drawn.lumProfile = {
        x: base.lumProfile.x.map(function (x) { return x * drawn.naturalWidth / base.naturalWidth; }),
        lum: base.lumProfile.lum
      };
      return drawn;
    }
    var merged = {};
    Object.keys(base).forEach(function (k) { merged[k] = base[k]; });
    Object.keys(scene).forEach(function (k) { merged[k] = scene[k]; });
    merged.sceneKey = selectedSceneKey;
    if (sharedFourPieceScene) {
      merged.anchorX = scene.naturalWidth / 2;
      merged.zone = { left: 0, right: scene.naturalWidth };
      // Four framed 40x60 pieces extend about 3 natural pixels past each edge.
      // Keep their true scale and accept this intentional crop.
      merged.allowHorizontalCrop = size.w >= 40;
    }
    return merged;
  }

  // Product-page width line: the full set's real width for the selected size and finish.
  // Returns { lead, width } so the width can be emphasised.
  function widthLine(sizeValue, framed, count, cfg, units) {
    var size = parseSize(sizeValue);
    if (!size) return null;
    return { lead: count > 1 ? 'Full set ' : '', width: formatLength(setDims(size, framed, count, cfg).w, units) + ' wide' };
  }

  // On the largest size only: its canvas surface area relative to the most popular size.
  // Returns { ratio, ref: { w, h } } or null.
  function surfaceComparison(sizeValue, sizeValues, mostPopular) {
    var size = parseSize(sizeValue);
    var largest = sizeValues[sizeValues.length - 1];
    var ref = null;
    for (var i = 0; i < (mostPopular || []).length; i++) {
      var v = String(mostPopular[i]).trim();
      if (sizeValues.indexOf(v) !== -1) { ref = v; break; }
    }
    var refSize = ref && parseSize(ref);
    if (!size || sizeValue !== largest || !refSize || ref === largest) return null;
    var ratio = Math.round((size.w * size.h) / (refSize.w * refSize.h) * 10) / 10;
    return ratio >= 1.5 ? { ratio: ratio, ref: refSize } : null;
  }

  function findVariant(variants, selected) {
    var wanted = selected.filter(Boolean);
    for (var i = 0; i < (variants || []).length; i++) {
      var opts = variants[i].options || [];
      var ok = wanted.every(function (v) { return opts.indexOf(v) !== -1; });
      if (ok) return variants[i];
    }
    return null;
  }

  var math = {
    parseSize: parseSize, pieceDims: pieceDims, setDims: setDims, sofaBackTop: sofaBackTop,
    placement: placement, formatFtIn: formatFtIn, formatCm: formatCm, formatLength: formatLength, lumAt: lumAt,
    lightingFactor: lightingFactor, sourceCrop: sourceCrop, findVariant: findVariant,
    sceneKey: sceneKey, sceneConfig: sceneConfig, recommendSize: recommendSize, recommendArrangement: recommendArrangement, widthLine: widthLine,
    surfaceComparison: surfaceComparison
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = math;
    return;
  }

  /* ---------- Browser component ---------- */

  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function findOptionName(options, test) {
    var names = Object.keys(options || {});
    for (var i = 0; i < names.length; i++) if (test(names[i].toLowerCase())) return names[i];
    return null;
  }

  function pageRadios(name) {
    return Array.prototype.slice.call(document.querySelectorAll('input[type="radio"][data-name]'))
      .filter(function (input) { return input.getAttribute('data-name') === name; });
  }

  function checkedValue(name) {
    var radios = pageRadios(name);
    for (var i = 0; i < radios.length; i++) if (radios[i].checked) return radios[i].value;
    return null;
  }

  function piecesFromValue(value, fallback) {
    var m = /(\d+)/.exec(value || '');
    return m ? parseInt(m[1], 10) : fallback;
  }

  function init() {
    var root = document.querySelector('[data-size-guide-visualizer]');
    var baseCfg = readJson('size-guide-visualizer-config');
    var cfg = baseCfg; // re-pointed to the active scene's config on every draw
    var data = readJson('size-guide-visualizer-data');
    if (!root || !baseCfg || !data || root.hasAttribute('data-size-guide-ready')) return;
    root.setAttribute('data-size-guide-ready', '');

    var canvas = root.querySelector('canvas');
    var ctx = canvas.getContext('2d');
    var stage = root.querySelector('[data-size-guide-stage]');
    var controls = root.querySelector('[data-size-guide-controls]');
    var chooseBtn = root.querySelector('[data-size-guide-choose]');
    var selectedEl = root.querySelector('[data-size-guide-selected]');
    var priceEl = root.querySelector('[data-size-guide-price]');
    var subline = null;
    var fitInput = root.querySelector('[data-size-guide-fit-input]');
    var fitUnit = root.querySelector('[data-size-guide-fit-unit]');
    var fitSubmit = root.querySelector('[data-size-guide-fit-submit]');
    var fitResult = root.querySelector('[data-size-guide-fit-result]');
    var fitModes = root.querySelectorAll('input[name="size-guide-fit-mode"]');
    var fitWidthIn = null; // last entered width, always stored in inches
    var fitToggle = root.querySelector('[data-size-guide-fit-toggle]');
    var fitPanel = root.querySelector('[data-size-guide-fit-panel]');
    var fitWhy = root.querySelector('[data-size-guide-fit-why]');
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var modal = document.getElementById('size-guide-modal');
    // The in/cm toggle lives in the modal header, outside the visualizer root.
    var unitButtons = (modal || document).querySelectorAll('[data-size-guide-units]');
    var description = modal && modal.querySelector('[data-size-guide-description]');
    var setTitle = modal && modal.querySelector('[data-size-guide-set-title]');
    var table = modal && modal.querySelector('.size-guide-table');
    var canvasText = modal && modal.querySelector('[data-size-guide-canvas-text]');
    var frameText = modal && modal.querySelector('[data-size-guide-frame-text]');
    var tableNote = modal && modal.querySelector('[data-size-guide-table-note]');

    var sizeName = findOptionName(data.options, function (n) { return n === 'size'; });
    // Most products call it Finish; a few call the same option Frame.
    var finishName = findOptionName(data.options, function (n) { return n === 'finish' || n === 'frame'; });
    var piecesName = findOptionName(data.options, function (n) { return n.indexOf('piece') !== -1; });
    if (!sizeName) return;

    var UNITS_KEY = 'percept-size-guide-units';
    var storedUnits = null;
    try { storedUnits = window.localStorage.getItem(UNITS_KEY); } catch (e) { storedUnits = null; }
    var state = {
      size: null, finish: null, pieces: null,
      // Default by the shopper's country (US, Liberia, Myanmar use inches); a toggled choice is remembered.
      units: storedUnits === 'cm' || storedUnits === 'in' ? storedUnits : (['US', 'LR', 'MM'].indexOf(data.country) !== -1 ? 'in' : 'cm')
    };
    function len(inches) { return formatLength(inches, state.units); }
    // Small construction measurements (frame, gaps, depth): "2.5 cm" rather than "3 cm".
    function smallLen(inches) {
      if (state.units !== 'cm') return (Math.round(inches) === inches ? String(inches) : String(Math.round(inches * 10) / 10)) + ' in';
      var cm = inches * 2.54;
      return (cm < 10 ? String(Math.round(cm * 10) / 10) : String(Math.round(cm))) + ' cm';
    }
    var images = [];
    var sceneImages = {}; // sceneKey -> Image | 'loading' | null
    var loaded = false;
    var texture = null;
    var money = null;
    try {
      money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: data.currency || 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0
      });
    } catch (e) { money = null; }

    // The guide always shows the framed version; finish stays whatever the page has selected.
    function framed() { return true; }
    function count() {
      return piecesName ? piecesFromValue(state.pieces, data.pieceCount) : data.pieceCount;
    }

    /* ----- image loading (on first open) ----- */
    function loadImage(src) {
      return new Promise(function (resolve) {
        if (!src) return resolve(null);
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }

    function ensureLoaded() {
      if (loaded) return;
      loaded = true;
      Promise.all(data.pieces.map(function (p) { return loadImage(p.image); })).then(function (results) {
        images = results;
        draw();
      });
    }

    /* ----- room ----- */
    function plasterTexture() {
      if (texture) return texture;
      var t = document.createElement('canvas');
      t.width = t.height = 192;
      var tc = t.getContext('2d');
      var id = tc.createImageData(192, 192);
      for (var i = 0; i < id.data.length; i += 4) {
        var v = Math.random() * 255;
        id.data[i] = id.data[i + 1] = id.data[i + 2] = v;
        id.data[i + 3] = 9;
      }
      tc.putImageData(id, 0, 0);
      texture = ctx.createPattern(t, 'repeat');
      return texture;
    }

    function rounded(x, y, w, h, rTop, rBottom) {
      var rb = rBottom || 0;
      ctx.beginPath();
      ctx.moveTo(x + rTop, y);
      ctx.lineTo(x + w - rTop, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + rTop);
      ctx.lineTo(x + w, y + h - rb);
      ctx.quadraticCurveTo(x + w, y + h, x + w - rb, y + h);
      ctx.lineTo(x + rb, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - rb);
      ctx.lineTo(x, y + rTop);
      ctx.quadraticCurveTo(x, y, x + rTop, y);
      ctx.closePath();
    }

    function softGradientFill(x, y, w, h, top, bottom) {
      var g = ctx.createLinearGradient(0, y, 0, y + h);
      g.addColorStop(0, top);
      g.addColorStop(1, bottom);
      ctx.fillStyle = g;
    }

    function drawRoom() {
      var W = cfg.naturalWidth, H = cfg.naturalHeight, ppi = cfg.pxPerInch;
      var floorY = cfg.floorY, ceilY = cfg.ceilingY;

      // Ceiling band
      ctx.fillStyle = '#171a19';
      ctx.fillRect(0, 0, W, ceilY);

      // Wall with light from the left
      ctx.fillStyle = '#2c302f';
      ctx.fillRect(0, ceilY, W, floorY - ceilY);
      var side = ctx.createLinearGradient(0, 0, W, 0);
      side.addColorStop(0, 'rgba(255, 244, 228, 0.07)');
      side.addColorStop(0.45, 'rgba(255, 244, 228, 0.0)');
      side.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
      ctx.fillStyle = side;
      ctx.fillRect(0, ceilY, W, floorY - ceilY);
      var topShade = ctx.createLinearGradient(0, ceilY, 0, ceilY + 26 * ppi);
      topShade.addColorStop(0, 'rgba(0, 0, 0, 0.28)');
      topShade.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = topShade;
      ctx.fillRect(0, ceilY, W, 26 * ppi);
      ctx.fillStyle = plasterTexture();
      ctx.fillRect(0, ceilY, W, floorY - ceilY);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(0, ceilY, W, 1.5);

      // Skirting and floor
      ctx.fillStyle = '#232625';
      ctx.fillRect(0, floorY - 3 * ppi, W, 3 * ppi);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(0, floorY - 3 * ppi, W, 1.5);
      softGradientFill(0, floorY, W, H - floorY, '#1a1c1b', '#111312');
      ctx.fillRect(0, floorY, W, H - floorY);
    }

    // Furniture is drawn as quiet wireframes, matching the person silhouette.
    var WIRE_FILL = 'rgba(255, 255, 255, 0.06)';
    var WIRE_STROKE = 'rgba(255, 255, 255, 0.28)';
    var WIRE_DETAIL = 'rgba(255, 255, 255, 0.16)';

    function wire(path, scale, fill) {
      path();
      ctx.fillStyle = fill || WIRE_FILL;
      ctx.fill();
      ctx.strokeStyle = WIRE_STROKE;
      ctx.lineWidth = 1.2 / scale;
      ctx.stroke();
    }

    function detailLine(x1, y1, x2, y2, scale) {
      ctx.strokeStyle = WIRE_DETAIL;
      ctx.lineWidth = 1 / scale;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    function drawSideTable(scale) {
      var t = cfg.sideTable;
      if (!t) return;
      var ppi = cfg.pxPerInch, w = t.widthIn * ppi, h = t.heightIn * ppi;
      var x = t.centerX - w / 2, y = cfg.floorY - h;
      ctx.save();
      wire(function () { rounded(x, y, w, h, 0.8 * ppi, 0.3 * ppi); }, scale);
      detailLine(x + 1.5 * ppi, y + 1.6 * ppi, x + w - 1.5 * ppi, y + 1.6 * ppi, scale);
      ctx.restore();
    }

    function drawSofa(scale) {
      var s = cfg.sofa, ppi = cfg.pxPerInch, floorY = cfg.floorY;
      var W = s.widthIn * ppi, x = cfg.anchorX - W / 2;
      var arm = s.armWidthIn * ppi, leg = s.legHeightIn * ppi;
      var baseTop = floorY - 11 * ppi;
      var seatTop = floorY - s.seatHeightIn * ppi;
      var backTop = floorY - s.backHeightIn * ppi;
      var armTop = floorY - s.armHeightIn * ppi;
      var innerX = x + arm, innerW = W - 2 * arm;
      var gap = 0.4 * ppi;
      ctx.save();

      // Legs
      [x + 2.5 * ppi, x + W - 4.5 * ppi].forEach(function (lx) {
        wire(function () { ctx.beginPath(); ctx.rect(lx, floorY - leg, 2 * ppi, leg); }, scale);
      });
      // Back cushions
      var seats = s.seats || 3;
      var bw = (innerW - (seats - 1) * gap) / seats;
      for (var i = 0; i < seats; i++) {
        (function (bx) {
          wire(function () { rounded(bx, backTop, bw, seatTop - backTop, 3 * ppi, 0); }, scale);
        })(innerX + i * (bw + gap));
      }
      // Base
      wire(function () { rounded(x, baseTop, W, floorY - leg - baseTop, 0.6 * ppi, 0.6 * ppi); }, scale);
      // Seat cushions
      var sw = (innerW - (seats - 1) * gap) / seats;
      for (var j = 0; j < seats; j++) {
        (function (sx) {
          wire(function () { rounded(sx, seatTop, sw, baseTop - seatTop, 1.4 * ppi, 0.8 * ppi); }, scale);
        })(innerX + j * (sw + gap));
      }
      // Arms
      [x, x + W - arm].forEach(function (ax) {
        wire(function () { rounded(ax, armTop, arm, floorY - leg - armTop, 2.2 * ppi, 0.6 * ppi); }, scale);
      });
      // Width label on the floor
      if (s.widthIn) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.font = '600 ' + (11 / scale) + 'px Montserrat, Lato, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(len(s.widthIn) + ' sofa', cfg.anchorX, floorY + (cfg.naturalHeight - floorY) / 2);
      }
      ctx.restore();
    }

    function drawPerson(scale) {
      var p = cfg.person;
      if (!p) return;
      var k = cfg.pxPerInch * (p.heightIn / 72); // path is authored for a 72 in figure
      var X = function (v) { return p.centerX + v * k; };
      var Y = function (v) { return p.floorY + v * k; };
      ctx.save();
      ctx.fillStyle = WIRE_FILL;
      ctx.strokeStyle = WIRE_STROKE;
      ctx.lineWidth = 1.2 / scale;
      ctx.beginPath();
      ctx.arc(X(0), Y(-67.4), 4.6 * k, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(X(-1.8), Y(-62.6));
      ctx.lineTo(X(1.8), Y(-62.6));
      ctx.lineTo(X(2.2), Y(-60.2));
      ctx.quadraticCurveTo(X(9), Y(-59.6), X(9.6), Y(-55.5));
      ctx.lineTo(X(10.6), Y(-33.5));
      ctx.quadraticCurveTo(X(10.6), Y(-31.2), X(8.9), Y(-31.8));
      ctx.lineTo(X(7.6), Y(-52.5));
      ctx.lineTo(X(7.1), Y(-35.5));
      ctx.lineTo(X(6.1), Y(0));
      ctx.lineTo(X(1.3), Y(0));
      ctx.lineTo(X(0.5), Y(-30.5));
      ctx.lineTo(X(-0.5), Y(-30.5));
      ctx.lineTo(X(-1.3), Y(0));
      ctx.lineTo(X(-6.1), Y(0));
      ctx.lineTo(X(-7.1), Y(-35.5));
      ctx.lineTo(X(-7.6), Y(-52.5));
      ctx.lineTo(X(-8.9), Y(-31.8));
      ctx.quadraticCurveTo(X(-10.6), Y(-31.2), X(-10.6), Y(-33.5));
      ctx.lineTo(X(-9.6), Y(-55.5));
      ctx.quadraticCurveTo(X(-9), Y(-59.6), X(-2.2), Y(-60.2));
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.font = '600 ' + (11 / scale) + 'px Montserrat, Lato, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(len(p.heightIn), X(-7.5), Y(-67.4));
      ctx.restore();
    }

    /* ----- artwork ----- */
    function drawArt(img, x, y, w, h) {
      if (img) {
        var c = sourceCrop(img.naturalWidth, img.naturalHeight, w, h, cfg.artInset);
        ctx.drawImage(img, c.sx, c.sy, c.sw, c.sh, x, y, w, h);
      } else {
        ctx.fillStyle = '#3a3e3d';
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
      }
    }

    function drawPiece(rect, img, deviceScale) {
      var ppi = cfg.pxPerInch;
      // Cast shadow on the wall (light from the left).
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
      ctx.shadowBlur = 1.4 * ppi * deviceScale;
      ctx.shadowOffsetX = 0.6 * ppi * deviceScale;
      ctx.shadowOffsetY = 0.8 * ppi * deviceScale;
      ctx.fillStyle = '#0c0d0d';
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.restore();

      if (framed()) {
        // Floating frame: slim black rail, a narrow dark gap, canvas inset by frameBorderIn.
        var border = cfg.frameBorderIn * ppi;
        var rail = (cfg.frameBorderIn - cfg.frameGapIn) * ppi;
        ctx.fillStyle = '#141515';
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
        ctx.fillStyle = '#060606';
        ctx.fillRect(rect.x + rail, rect.y + rail, rect.w - 2 * rail, rect.h - 2 * rail);
        // Rail highlights on the lit edges.
        ctx.fillStyle = 'rgba(255, 255, 255, 0.14)';
        ctx.fillRect(rect.x, rect.y, rect.w, 1.2);
        ctx.fillRect(rect.x, rect.y, 1.2, rect.h);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
        ctx.fillRect(rect.x + rail - 1.2, rect.y + rail - 1.2, rect.w - 2 * rail + 2.4, 1.2);
        // Canvas face, casting a tiny shadow into the gap.
        var cx = rect.x + border, cy = rect.y + border, cw = rect.w - 2 * border, ch = rect.h - 2 * border;
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 0.2 * ppi * deviceScale;
        ctx.shadowOffsetX = 0.08 * ppi * deviceScale;
        ctx.shadowOffsetY = 0.1 * ppi * deviceScale;
        ctx.fillStyle = '#000';
        ctx.fillRect(cx, cy, cw, ch);
        ctx.restore();
        drawArt(img, cx, cy, cw, ch);
      } else {
        // Gallery wrapped: the canvas alone, with its depth hinted on the unlit edges.
        drawArt(img, rect.x, rect.y, rect.w, rect.h);
        var d = 0.12 * ppi;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
        ctx.fillRect(rect.x + rect.w - d, rect.y, d, rect.h);
        ctx.fillRect(rect.x, rect.y + rect.h - d, rect.w, d);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.fillRect(rect.x, rect.y, rect.w, 1);
        ctx.fillRect(rect.x, rect.y, 1, rect.h);
      }

      // Match the room's light falloff at this position.
      var f = lightingFactor(rect.x + rect.w / 2, cfg);
      ctx.save();
      if (f < 1) {
        var c = Math.round(255 * f);
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgb(' + c + ',' + c + ',' + c + ')';
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      } else if (f > 1) {
        var s = Math.round(255 * (f - 1) * 0.5);
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = 'rgb(' + s + ',' + s + ',' + s + ')';
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      }
      ctx.restore();
    }

    function drawDimension(rect, widthIn, scale) {
      var ppi = cfg.pxPerInch;
      var y = rect.y - 3 * ppi;
      var tick = 0.9 * ppi;
      var fontPx = 12 / scale;
      var label = len(widthIn) + ' wide';
      ctx.save();
      ctx.font = '600 ' + fontPx + 'px Montserrat, Lato, sans-serif';
      var tw = ctx.measureText(label).width;
      var padX = 9 / scale, padY = 5 / scale;
      var pillW = tw + 2 * padX, pillH = fontPx + 2 * padY;
      var cx = rect.x + rect.w / 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = 1 / scale;
      ctx.beginPath();
      ctx.moveTo(rect.x, y - tick); ctx.lineTo(rect.x, y + tick);
      ctx.moveTo(rect.x + rect.w, y - tick); ctx.lineTo(rect.x + rect.w, y + tick);
      if (rect.w > pillW + 8 / scale) {
        ctx.moveTo(rect.x, y); ctx.lineTo(cx - pillW / 2 - 4 / scale, y);
        ctx.moveTo(cx + pillW / 2 + 4 / scale, y); ctx.lineTo(rect.x + rect.w, y);
      }
      ctx.stroke();
      ctx.fillStyle = 'rgba(17, 19, 18, 0.9)';
      rounded(cx - pillW / 2, y - pillH / 2, pillW, pillH, pillH / 2, pillH / 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, y + 0.5 / scale);
      ctx.restore();
    }

    function sceneImage(key, src) {
      var cached = sceneImages[key];
      if (cached && cached !== 'loading') return cached;
      if (!cached) {
        sceneImages[key] = 'loading';
        loadImage(src).then(function (img) { sceneImages[key] = img; draw(); });
      }
      return null;
    }

    function drawFurnitureLine(scale) {
      var f = cfg.furniture;
      if (!f) return;
      var tick = 0.9 * cfg.pxPerInch;
      var label = len(f.widthIn) + ' ' + f.name;
      ctx.save();
      ctx.font = '600 ' + (12 / scale) + 'px Montserrat, Lato, sans-serif';
      var tw = ctx.measureText(label).width + 16 / scale;
      var cx = (f.left + f.right) / 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 1 / scale;
      ctx.beginPath();
      ctx.moveTo(f.left, f.y - tick); ctx.lineTo(f.left, f.y + tick);
      ctx.moveTo(f.right, f.y - tick); ctx.lineTo(f.right, f.y + tick);
      ctx.moveTo(f.left, f.y); ctx.lineTo(cx - tw / 2, f.y);
      ctx.moveTo(cx + tw / 2, f.y); ctx.lineTo(f.right, f.y);
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      ctx.shadowBlur = 6;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, f.y);
      ctx.restore();
    }

    // Snapshot the current frame over the canvas and fade it out, so size changes blend.
    function crossfade() {
      if (reducedMotion || !canvas.width || !canvas.height) return null;
      var snap = document.createElement('canvas');
      snap.width = canvas.width;
      snap.height = canvas.height;
      snap.className = 'size-guide-stage__fade';
      snap.getContext('2d').drawImage(canvas, 0, 0);
      stage.appendChild(snap);
      return snap;
    }

    function releaseFade(snap) {
      if (!snap) return;
      requestAnimationFrame(function () {
        snap.style.opacity = '0';
        setTimeout(function () { if (snap.parentNode) snap.parentNode.removeChild(snap); }, 260);
      });
    }

    function draw(opts) {
      var size = parseSize(state.size);
      var n = count();
      var next = sceneConfig(baseCfg, size, n);
      var photo = next.sceneKey ? sceneImage(next.sceneKey, next.image) : null;
      // Keep the previous frame on screen until a new scene photo has loaded.
      if (next.sceneKey && !photo) return;
      var snap = opts && opts.fade ? crossfade() : null;
      cfg = next;
      stage.style.aspectRatio = cfg.naturalWidth + ' / ' + cfg.naturalHeight;
      if (cfg.naturalHeight > cfg.naturalWidth) stage.setAttribute('data-portrait', '');
      else stage.removeAttribute('data-portrait');

      var cssW = stage.clientWidth || canvas.clientWidth;
      if (!cssW) return;
      var cssH = cssW * cfg.naturalHeight / cfg.naturalWidth;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var pxW = Math.round(cssW * dpr), pxH = Math.round(cssH * dpr);
      if (canvas.width !== pxW) canvas.width = pxW;
      if (canvas.height !== pxH) canvas.height = pxH;
      canvas.style.height = cssH + 'px';
      var scale = cssW / cfg.naturalWidth;
      var deviceScale = scale * dpr;
      ctx.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);
      ctx.clearRect(0, 0, cfg.naturalWidth, cfg.naturalHeight);

      if (cfg.sceneKey) {
        ctx.drawImage(photo, 0, 0, cfg.naturalWidth, cfg.naturalHeight);
        drawFurnitureLine(scale);
      } else {
        drawRoom();
        drawSideTable(scale);
        drawSofa(scale);
        drawPerson(scale);
      }

      if (size) {
        var set = setDims(size, framed(), n, cfg);
        var rect = placement(set, cfg);
        if (!rect.fitsVertically || (!rect.fitsZone && !cfg.allowHorizontalCrop)) {
          console.warn('[size-guide] set exceeds the configured wall zone', cfg.sceneKey || 'drawn', set, rect);
        }
        var ppi = cfg.pxPerInch;
        for (var i = 0; i < n; i++) {
          drawPiece({
            x: rect.x + i * (set.piece.w + cfg.pieceGapIn) * ppi,
            y: rect.y,
            w: set.piece.w * ppi,
            h: set.piece.h * ppi
          }, images.length ? images[i % images.length] : null, deviceScale);
        }
        drawDimension(rect, set.w, scale);
        var context = cfg.furniture
          ? ' above a ' + len(cfg.furniture.widthIn) + ' ' + cfg.furniture.name
          : (cfg.sceneKey ? ' in a room' : ' above a ' + len(cfg.sofa.widthIn) + ' sofa next to a ' + len(72) + ' person');
        canvas.setAttribute('aria-label', describe(size, n, set) + ', shown' + context);
      }
      releaseFade(snap);
    }

    /* ----- text ----- */
    function dims(wIn, hIn) {
      return formatFtIn(wIn) + ' × ' + formatFtIn(hIn) + ' (' +
        Math.round(wIn * 2.54) + ' × ' + Math.round(hIn * 2.54) + ' cm)';
    }

    // Width x height in the selected unit: "7 ft 3 in × 5 ft 2 in" or "221 × 157 cm".
    function dimsU(wIn, hIn) {
      return state.units === 'cm'
        ? Math.round(wIn * 2.54) + ' \u00d7 ' + Math.round(hIn * 2.54) + ' cm'
        : formatFtIn(wIn) + ' \u00d7 ' + formatFtIn(hIn);
    }

    // Plain size in the selected unit: "24×36 in" or "61×91 cm".
    function plainDims(wIn, hIn) {
      return state.units === 'cm'
        ? Math.round(wIn * 2.54) + '\u00d7' + Math.round(hIn * 2.54) + ' cm'
        : wIn + '\u00d7' + hIn + ' in';
    }

    function sizeLabel(size) { return size.w + '×' + size.h; }

    function describe(size, n, set) {
      var what = n > 1 ? n + '-piece set' : 'Single piece';
      return what + ' · ' + sizeLabel(size) + (state.finish ? ' · ' + state.finish : '') + ' — ' + dims(set.w, set.h);
    }

    function currentPrice() {
      var v = findVariant(data.variants, [state.pieces, state.size, state.finish]);
      if (!v || !money) return null;
      return money.format(Math.round(v.price / 100));
    }

    function sizeInUnits(size) {
      return state.units === 'cm'
        ? Math.round(size.w * 2.54) + '\u00d7' + Math.round(size.h * 2.54) + ' cm'
        : size.w + '\u00d7' + size.h + ' in';
    }

    function updateText() {
      var size = parseSize(state.size);
      if (!size) return;
      var n = count();
      var set = setDims(size, framed(), n, cfg);
      if (subline) {
        subline.textContent = n > 1
          ? n + '-piece set \u00b7 ' + len(set.w) + ' total width'
          : 'Single piece \u00b7 ' + len(set.w) + ' wide';
      }
      if (selectedEl) selectedEl.textContent = sizeInUnits(size);
      if (priceEl) priceEl.textContent = currentPrice() || '';
      if (description) {
        var facts = n > 1
          ? [['Pieces', n + ' artworks, each ' + plainDims(size.w, size.h)],
             ['Full set', dimsU(set.w, set.h)]]
          : [['Piece', '1 artwork, ' + plainDims(size.w, size.h)],
             ['Framed size', dimsU(set.w, set.h)]];
        description.innerHTML = facts.map(function (f) {
          return '<div><dt>' + f[0] + '</dt><dd>' + f[1] + '</dd></div>';
        }).join('') + (n > 1
          ? '<p class="size-guide-facts__note">Includes ' + smallLen(cfg.frameBorderIn) + ' of frame on every side of each piece and ' + smallLen(cfg.pieceGapIn) + ' of spacing between pieces.</p>'
          : '');
        if (setTitle) setTitle.textContent = n > 1 ? 'Your set' : 'Your piece';
      }
      if (canvasText) {
        canvasText.textContent = 'Stretched over a solid wood frame, ' + smallLen(1.5) +
          ' deep, and shipped ready to hang.';
      }
      if (frameText) {
        var framedPiece = pieceDims(size, true, cfg.frameBorderIn);
        frameText.textContent = 'Adds ' + smallLen(cfg.frameBorderIn) + ' on every side. A ' +
          plainDims(size.w, size.h) + ' canvas becomes ' + plainDims(framedPiece.w, framedPiece.h) + ' framed.';
      }
      Array.prototype.forEach.call(unitButtons, function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-size-guide-units') === state.units));
      });
      updateTable();
      renderFit();
    }

    function unitValue(v) { return state.units === 'cm' ? Math.round(v * 2.54) + ' cm' : v + ' in'; }

    function updateTable() {
      if (!table) return;
      var n = count();
      var showSet = n > 1;
      var head = table.querySelector('thead tr');
      var body = table.querySelector('tbody');
      if (!head || !body) return;
      head.innerHTML = '<th>Size</th><th>Width</th><th>Height</th>' + (showSet ? '<th>Full set</th>' : '<th>Framed</th>');
      body.innerHTML = '';
      (data.options[sizeName] || []).forEach(function (value) {
        var s2 = parseSize(value);
        if (!s2) return;
        var tr = document.createElement('tr');
        tr.setAttribute('data-guide-size', value);
        if (value === state.size) tr.setAttribute('aria-current', 'true');
        var set = setDims(s2, true, n, cfg);
        tr.innerHTML =
          '<td><button type="button" class="size-guide-row-button">' + s2.w + '\u00d7' + s2.h + '</button></td>' +
          '<td>' + unitValue(s2.w) + '</td>' +
          '<td>' + unitValue(s2.h) + '</td>' +
          '<td>' + (state.units === 'cm'
            ? Math.round(set.w * 2.54) + ' \u00d7 ' + Math.round(set.h * 2.54) + ' cm'
            : set.w + ' \u00d7 ' + set.h + ' in') + '</td>';
        tr.addEventListener('click', function () { setState('size', value); });
        body.appendChild(tr);
      });
      if (tableNote) {
        tableNote.textContent = 'Width and height are per panel, unframed. ' +
          (showSet ? 'Full set includes ' + smallLen(cfg.frameBorderIn) + ' of frame per side and ' + smallLen(cfg.pieceGapIn) + ' between pieces.'
                   : 'Framed adds ' + smallLen(cfg.frameBorderIn) + ' on every side.');
      }
    }

    /* ----- controls: compact segmented selectors ----- */
    function group(label, key, values, hint) {
      var item = document.createElement('div');
      item.className = 'size-guide-group size-guide-group--' + key;
      var head = document.createElement('div');
      head.className = 'size-guide-group__head';
      var title = document.createElement('span');
      title.className = 'size-guide-group__title';
      title.textContent = label;
      head.appendChild(title);
      if (hint) {
        var h = document.createElement('span');
        h.className = 'size-guide-group__hint';
        h.textContent = hint;
        head.appendChild(h);
      }
      item.appendChild(head);
      var seg = document.createElement('div');
      seg.className = 'size-guide-segmented';
      seg.setAttribute('role', 'radiogroup');
      seg.setAttribute('aria-label', label);
      values.forEach(function (value, i) {
        var id = 'size-guide-' + key + '-' + i;
        var input = document.createElement('input');
        input.type = 'radio';
        input.id = id;
        input.name = 'size-guide-' + key;
        input.value = value;
        input.className = 'size-guide-segmented__input';
        // Detached from the product form so it is never submitted with an order.
        input.setAttribute('form', 'size-guide-detached');
        input.setAttribute('data-size-guide-key', key);
        input.addEventListener('change', function () { if (input.checked) setState(key, value); });
        var lab = document.createElement('label');
        lab.setAttribute('for', id);
        lab.className = 'size-guide-segmented__option';
        var sz = key === 'size' ? parseSize(value) : null;
        lab.textContent = sz ? sz.w + '\u00d7' + sz.h : value;
        seg.appendChild(input);
        seg.appendChild(lab);
      });
      item.appendChild(seg);
      if (key === 'size') {
        subline = document.createElement('p');
        subline.className = 'size-guide-group__note';
        subline.setAttribute('aria-live', 'polite');
        item.appendChild(subline);
      }
      controls.appendChild(item);
    }

    function syncOptions() {
      Array.prototype.forEach.call(controls.querySelectorAll('input[data-size-guide-key]'), function (input) {
        input.checked = state[input.getAttribute('data-size-guide-key')] === input.value;
      });
    }

    /* ----- fit helper ----- */
    var VERDICTS = { small: 'Looks small', good: 'Good fit', tight: 'Closer fit', wide: 'Too wide' };

    function fitMode() {
      for (var i = 0; i < fitModes.length; i++) if (fitModes[i].checked) return fitModes[i].value;
      return 'furniture';
    }

    function readFitWidth() {
      if (!fitInput) return null;
      var v = parseFloat(String(fitInput.value).replace(',', '.'));
      if (!(v > 0) || !isFinite(v)) return null;
      return state.units === 'cm' ? v / 2.54 : v;
    }

    function renderArrangementFit() {
      var rec = recommendArrangement(data.options[sizeName] || [], framed(), baseCfg, fitWidthIn, fitMode(), data.arrangementPrices);
      if (!rec) { fitResult.hidden = true; return; }
      var best = rec.best;
      function arrangementLabel(r) {
        return (r.count === 1 ? 'One piece' : r.count + '-piece set') + ' in ' + sizeLabel(parseSize(r.value));
      }
      var headline = rec.fits ? 'Best fit: ' + arrangementLabel(best) : 'No ideal fit at this width';
      var reason = rec.mode === 'wall'
        ? (rec.fits ? 'Framed, this spans ' + len(best.setW) + ', leaving about ' + len(best.sideIn) + ' on each side.' : 'Try a wider wall, or place smaller artwork above furniture.')
        : (rec.fits ? 'Framed, this spans ' + len(best.setW) + '—about ' + Math.round(best.ratio * 100) + '% of your furniture’s width.' : 'The available arrangements fall outside our recommended proportions. Try another wall or a different furniture width.');
      var rows = rec.layouts.map(function (r) {
        var isBest = rec.fits && r === best;
        return '<li class="size-guide-fit__row' + (isBest ? ' is-best' : '') + '"><span class="size-guide-fit__row-size">' + arrangementLabel(r) + '</span><span class="size-guide-fit__row-width">' + len(r.setW) + ' wide</span><span class="size-guide-fit__row-verdict">' + (isBest ? 'Best fit' : VERDICTS[r.verdict]) + '</span></li>';
      }).join('');
      var showing = best.value === state.size && best.count === count();
      fitResult.innerHTML = '<p class="size-guide-fit__headline">' + headline + '</p><p class="size-guide-fit__reason">' + reason + '</p>' +
        (rec.narrowWall && rec.fits ? '<p class="size-guide-fit__note">A smaller piece can work on a narrow stretch of wall like this. On a wider open wall, choose at least 24 × 36 inches for more height and presence.</p>' : '') +
        '<p class="size-guide-fit__note">Sizes are per piece. Widths include Premium Frame and 3-inch gaps. Check the height on your wall too.</p>' +
        '<ul class="size-guide-fit__rows">' + rows + '</ul>' +
        (rec.fits ? '<button type="button" class="size-guide-fit__show" data-arrangement-show' + (showing ? ' disabled>Showing this arrangement' : '>Preview this arrangement') + '</button>' : '');
      fitResult.hidden = false;
      var show = fitResult.querySelector('[data-arrangement-show]');
      if (show && !show.disabled) show.addEventListener('click', function () {
        state.pieces = best.count + (best.count === 1 ? ' piece' : ' pieces');
        setState('size', best.value);
        stage.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      });
    }

    function renderFit() {
      if (!fitResult) return;
      if (fitUnit) fitUnit.textContent = state.units;
      if (fitWhy) fitWhy.textContent = fitWhy.getAttribute(fitMode() === 'wall' ? 'data-wall' : 'data-furniture');
      if (fitInput) fitInput.placeholder = state.units === 'cm' ? 'e.g. 213' : 'e.g. 84';
      if (!fitWidthIn) { fitResult.hidden = true; fitResult.innerHTML = ''; return; }
      if (data.educational) { renderArrangementFit(); return; }
      var rec = recommendSize(data.options[sizeName] || [], count(), framed(), baseCfg, fitWidthIn, fitMode());
      if (!rec) { fitResult.hidden = true; return; }
      var best = rec.best;
      var bestSize = parseSize(best.value);
      var pct = Math.round(best.ratio * 100);
      var measured = 'the ' + len(fitWidthIn) + ' you measured';
      var subject = count() > 1 ? 'the set spans ' : 'the piece is ';
      var reason;
      var note = '';
      var headline = (rec.fits ? 'We recommend ' : 'Closest fit: ') + '<strong>' + sizeLabel(bestSize) + '</strong>';
      if (rec.mode === 'wall') {
        if (rec.fits) {
          reason = (framed() ? 'Framed, ' : '') + subject + len(best.setW) + ' wide, leaving about ' + len(rec.sideIn) + ' of wall on each side.';
          if (rec.tight) {
            note = '<p class="size-guide-fit__note">This is a closer fit. A foot of clear wall on each side is ideal, but this size still leaves at least ' + len(WALL_MIN_MARGIN_IN) + ' per side.</p>';
          }
          if (rec.spacious) {
            note = '<p class="size-guide-fit__note">On a wall this size, ' + sizeLabel(bestSize) +
              ' becomes a gallery-style statement with open space around it.</p>';
            if (rec.expansion) {
              var expansion = rec.expansion;
              note += '<p class="size-guide-fit__note">Want to fill more of the wall? A ' + expansion.count +
                '-piece set in ' + sizeLabel(parseSize(expansion.value)) + ' spans ' + len(expansion.setW) +
                ', leaving about ' + len(expansion.sideIn) + ' on each side.' +
                (expansion.tight ? ' This is a closer fit.' : '') +
                ' <a class="size-guide-fit__link" href="/collections/bundle-collection-sets?pieces=' + expansion.count +
                '">See ' + expansion.count + '-piece sets</a></p>';
            }
          }
        } else {
          headline = rec.alternatives.length ? 'Go with fewer, larger pieces' : 'Try a different space';
          reason = best.verdict === 'small'
            ? 'The smaller sizes fit across this wall, but would look undersized in an open space. We recommend at least ' + sizeLabel({ w: 24, h: 36 }) + ' per piece.'
            : 'This layout needs more width to leave enough clear wall on each side.';
          rec.alternatives.forEach(function (alternative) {
            var singleLink = '';
            if (alternative.count === 1) {
              var collectionLink = document.createElement('a');
              collectionLink.className = 'size-guide-fit__link';
              collectionLink.href = data.individualCollectionUrl || data.allCollectionsUrl || '/collections';
              collectionLink.textContent = data.individualCollectionUrl
                ? 'Shop individual pieces from this collection' : 'Explore all collections';
              singleLink = ' ' + collectionLink.outerHTML;
            }
            note += '<p class="size-guide-fit__note"><strong>' +
              (alternative.count === 1 ? 'One piece' : alternative.count + '-piece set') + ' in ' +
              sizeLabel(parseSize(alternative.value)) + '</strong>: ' + len(alternative.setW) +
              ' wide, leaving about ' + len(alternative.sideIn) + ' on each side.' +
              (alternative.tight ? ' This is a closer fit.' : '') +
              (alternative.count > 1 ? ' <a class="size-guide-fit__link" href="/collections/bundle-collection-sets?pieces=' + alternative.count + '">See ' + alternative.count + '-piece sets</a>' : singleLink) + '</p>';
          });
          if (!rec.alternatives.length) note = '<p class="size-guide-fit__note">Try a wider wall, or place smaller artwork above furniture where it has something to anchor it.</p>';
        }
      } else if (rec.fits) {
        reason = 'Framed, ' + subject + len(best.setW) + ' wide, about ' + pct + '% of ' + measured + '.';
      } else if (rec.betweenSizes) {
        reason = 'Your measurement falls between sizes: smaller options sit below the ideal proportion, while larger ones go above it. ' +
          sizeLabel(bestSize) + ' is closest, at ' + pct + '% of ' + measured + '.';
      } else if (best.verdict === 'wide') {
        reason = 'Every size is wider than ideal for ' + measured + '. ' +
          sizeLabel(bestSize) + ' comes closest, at ' + pct + '% of that width.';
      } else {
        reason = 'Every size stays under the ideal for ' + measured + '. ' +
          sizeLabel(bestSize) + ' fills it best, at ' + pct + '% of that width.';
      }
      var rows = rec.rows.map(function (r) {
        var sz = parseSize(r.value);
        var cls = 'size-guide-fit__row' + (r.best ? ' is-best' : '') + ' is-' + r.verdict;
        var verdict = r.best && rec.fits ? (rec.tight ? 'Closer fit' : 'Best fit') : (rec.mode === 'wall' && r.verdict === 'good' ? 'Fits' : VERDICTS[r.verdict]);
        return '<li class="' + cls + '"><span class="size-guide-fit__row-size">' + sizeLabel(sz) + '</span>' +
          '<span class="size-guide-fit__row-width">' + len(r.setW) + '</span>' +
          '<span class="size-guide-fit__row-verdict">' + verdict + '</span></li>';
      }).join('');
      fitResult.innerHTML =
        '<p class="size-guide-fit__headline">' + headline + '</p>' +
        '<p class="size-guide-fit__reason">' + reason + '</p>' +
        note +
        '<ul class="size-guide-fit__rows">' + rows + '</ul>' +
        ((rec.mode !== 'wall' || rec.fits) ? '<button type="button" class="size-guide-fit__show" data-size-guide-fit-show="' + best.value + '"' +
          (best.value === state.size ? ' disabled>Showing ' : '>Show ') + sizeLabel(bestSize) + ' on the wall</button>' : '');
      fitResult.hidden = false;
      var show = fitResult.querySelector('[data-size-guide-fit-show]');
      if (show && !show.disabled) show.addEventListener('click', function () { setState('size', show.getAttribute('data-size-guide-fit-show')); });
    }

    // Analytics: dataLayer events for GTM, which forwards them to GA4.
    function track(name, params) {
      try {
        params.event = name;
        params.product_handle = data.handle;
        params.product_id = data.productId;
        params.page_path = window.location.pathname;
        (window.dataLayer = window.dataLayer || []).push(params);
      } catch (e) { /* analytics must never break the guide */ }
    }

    function runFit() {
      fitWidthIn = readFitWidth();
      renderFit();
      if (!fitWidthIn) {
        if (data.educational) { fitResult.textContent = 'Enter a width greater than zero.'; fitResult.hidden = false; }
        return;
      }
      if (data.educational) {
        var arrangement = recommendArrangement(data.options[sizeName] || [], framed(), baseCfg, fitWidthIn, fitMode(), data.arrangementPrices);
        track('size_guide_fit', { guide_source: 'buying_guide', guide_fit_mode: fitMode(), guide_fit_width_in: Math.round(fitWidthIn), guide_recommended_pieces: arrangement && arrangement.fits ? arrangement.best.count : null, guide_recommended_size: arrangement && arrangement.fits ? arrangement.best.value : null });
        return;
      }
      var rec = recommendSize(data.options[sizeName] || [], count(), framed(), baseCfg, fitWidthIn, fitMode());
      track('size_guide_fit', {
        guide_fit_mode: fitMode(),
        guide_fit_width_in: Math.round(fitWidthIn),
        guide_recommended_size: rec && (rec.mode !== 'wall' || rec.fits) ? rec.best.value : null,
        guide_alternative_pieces: rec ? rec.alternatives.map(function (a) { return a.count; }).join(',') : '',
        guide_fit_found: rec ? rec.fits : false
      });
    }

    function setState(key, value) {
      state[key] = value;
      syncOptions();
      updateText();
      draw({ fade: true });
    }

    function readPage() {
      state.size = data.educational ? '24x36' : (checkedValue(sizeName) || (data.options[sizeName] || [])[0]);
      if (finishName) state.finish = checkedValue(finishName) || (data.options[finishName] || [])[0];
      if (piecesName) state.pieces = data.educational ? '2 pieces' : (checkedValue(piecesName) || (data.options[piecesName] || [])[0]);
    }

    function applyToPage() {
      track('size_guide_choose', {
        guide_pieces: count(),
        guide_size: state.size,
        guide_size_changed: state.size !== checkedValue(sizeName) || (!!piecesName && state.pieces !== checkedValue(piecesName))
      });
      [[piecesName, state.pieces], [sizeName, state.size]].forEach(function (pair) {
        var name = pair[0], value = pair[1];
        if (!name || !value) return;
        var radios = pageRadios(name).filter(function (r) { return r.value === value; });
        if (!radios.length) return;
        var first = radios[0];
        var label = first.id && document.querySelector('label[for="' + first.id + '"]');
        if (label && !first.checked) label.click();
        radios.forEach(function (r) {
          if (!r.checked) {
            r.checked = true;
            r.dispatchEvent(new Event('input', { bubbles: true }));
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      var close = document.getElementById('close-size-guide');
      if (close) close.click();
    }

    if (piecesName) group('Pieces', 'pieces', data.options[piecesName]);
    group('Artwork size', 'size', data.options[sizeName], 'Per panel');
    Array.prototype.forEach.call(unitButtons, function (b) {
      b.addEventListener('click', function () {
        state.units = b.getAttribute('data-size-guide-units');
        if (fitInput && fitWidthIn) {
          fitInput.value = state.units === 'cm' ? Math.round(fitWidthIn * 2.54) : Math.round(fitWidthIn);
        }
        try { window.localStorage.setItem(UNITS_KEY, state.units); } catch (e) { /* storage unavailable */ }
        updateText();
        draw();
        updateWidthLine();
      });
    });

    /* ----- product-page width line (follows the page's own size/finish/pieces) ----- */
    var widthLineEl = document.querySelector('[data-size-width-line]');
    var widthLineText = widthLineEl && widthLineEl.querySelector('[data-size-width-line-text]');
    // Piece count on the page: a "Pieces" option, or the "Set" option (1-Piece / 3-Piece) some products use.
    var setOptionName = piecesName || findOptionName(data.options, function (n) { return n === 'set'; });
    function updateWidthLine() {
      if (!widthLineEl || !widthLineText) return;
      var sizeValue = checkedValue(sizeName);
      var finishValue = finishName ? checkedValue(finishName) : null;
      var n = setOptionName ? piecesFromValue(checkedValue(setOptionName), data.pieceCount) : data.pieceCount;
      // A single artwork has no set width worth drawing; above four pieces the hanging layout
      // is not a single row, so a one-row width would be wrong.
      if (n < 2 || n > 4) { widthLineEl.hidden = true; return; }
      var line = widthLine(sizeValue, /frame/i.test(finishValue || ''), n, baseCfg, state.units);
      if (!line) { widthLineEl.hidden = true; return; }
      var text = line.lead + line.width;
      function render() {
        widthLineText.textContent = '';
        widthLineText.appendChild(document.createTextNode(line.lead));
        var strong = document.createElement('strong');
        strong.textContent = line.width;
        widthLineText.appendChild(strong);
      }
      if (widthLineEl.hidden) { render(); widthLineEl.setAttribute('data-text', text); widthLineEl.hidden = false; return; }
      if (widthLineEl.getAttribute('data-text') === text) return;
      widthLineEl.setAttribute('data-text', text);
      // Swap the text instantly, like the price does: no fade, no animated width.
      render();
    }
    if (widthLineEl) {
      document.addEventListener('change', function (e) {
        var name = e.target && e.target.getAttribute && e.target.getAttribute('data-name');
        if (name && (name === sizeName || name === finishName || name === setOptionName)) updateWidthLine();
      });
      widthLineEl.addEventListener('click', function () {
        if (window.openSizeGuide) window.openSizeGuide('width_line');
      });
      updateWidthLine();
    }

    if (chooseBtn) chooseBtn.addEventListener('click', applyToPage);
    if (fitSubmit) fitSubmit.addEventListener('click', runFit);
    if (fitToggle && fitPanel) {
      fitToggle.addEventListener('click', function () {
        var open = fitToggle.getAttribute('aria-expanded') !== 'true';
        fitToggle.setAttribute('aria-expanded', String(open));
        root.querySelector('[data-size-guide-fit]').classList.toggle('is-open', open);
        if (open) {
          fitPanel.removeAttribute('inert');
          if (fitInput) setTimeout(function () { fitInput.focus({ preventScroll: true }); }, reducedMotion ? 0 : 220);
        } else {
          fitPanel.setAttribute('inert', '');
        }
      });
    }
    if (fitInput) fitInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); runFit(); }
    });
    Array.prototype.forEach.call(fitModes, function (r) {
      r.addEventListener('change', renderFit);
    });

    function onOpen(e) {
      readPage();
      track('size_guide_open', {
        guide_source: (e && e.detail && e.detail.source) || 'other',
        guide_pieces: count(),
        guide_size: state.size
      });
      syncOptions();
      updateText();
      ensureLoaded();
      // Warm both scenes for this product's piece count so switching sizes never waits on a photo.
      var n = count();
      ['large', 'small'].forEach(function (cls) {
        var sc = baseCfg.scenes && baseCfg.scenes[n + '-' + cls];
        if (sc) sceneImage(n + '-' + cls, sc.image);
      });
      requestAnimationFrame(function () { draw(); });
    }
    document.addEventListener('size-guide:open', onOpen);

    var resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(draw, 80);
    }
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(onResize).observe(stage);
    else window.addEventListener('resize', onResize);

    window.PerceptSizeGuide = { math: math, state: state, redraw: draw, images: function () { return images; } };
    if (data.educational) onOpen({ detail: { source: "buying_guide" } });
  }

  window.PerceptSizeGuide = { math: math };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
